import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { GlassCard } from '@/components/ui'
import { useAuthStore } from '../stores/authStore'
import { useLocale } from '../hooks/useLocale'
import {
  Key,
  Globe,
  Lock,
  ChevronRight,
  Check,
  AlertCircle,
  CreditCard,
} from 'lucide-react'
import AidSessions from '../components/AidSessions'
import WalletSignInPanel from '../components/auth/WalletSignInPanel'

/**
 * Dashboard - Minimal identity management
 *
 * "Simplicity is the ultimate sophistication." — Leonardo da Vinci
 *
 * User-facing features only. Staff tools live elsewhere.
 */

type TabId = 'identity' | 'wallet' | 'security' | 'connections'

export default function DashboardPage() {
  const navigate = useNavigate()
  const { t, tNested, tDeep, tDeeper } = useLocale()
  const { user, isAuthenticated, logout, isLoading } = useAuthStore()
  const [activeTab, setActiveTab] = useState<TabId>('identity')

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login')
    }
  }, [isAuthenticated, isLoading, navigate])

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  if (isLoading || !user) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            className="w-8 h-8 border border-white/20 border-t-white/60 mx-auto mb-6"
            style={{ borderRadius: '50%' }}
          />
          <p className="text-white/30 text-sm font-light tracking-wide">
            {t('dashboard', 'loading')}
          </p>
        </motion.div>
      </div>
    )
  }

  const tabs: { id: TabId; label: string }[] = [
    { id: 'identity',    label: tNested('dashboard', 'tabs', 'identity') },
    { id: 'wallet',      label: tNested('dashboard', 'tabs', 'wallet') },
    { id: 'security',    label: tNested('dashboard', 'tabs', 'security') },
    { id: 'connections', label: tNested('dashboard', 'tabs', 'connections') },
  ]

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-8 py-12 lg:py-16">
        {/* User Identity Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="flex items-start gap-6">
            {/* Avatar */}
            <div className="w-16 h-16 border border-white/10 flex items-center justify-center text-2xl font-extralight text-white/60">
              {user.displayName?.[0]?.toUpperCase() || user.username?.[0]?.toUpperCase() || 'Λ'}
            </div>

            {/* Identity Info */}
            <div className="flex-1">
              <h1 className="text-3xl font-extralight tracking-tight text-white mb-2">
                {user.displayName || user.username}
              </h1>
              <p className="text-white/40 font-light text-sm mb-3">
                {user.email}
              </p>

              {/* Status badges */}
              <div className="flex items-center gap-4">
                {user.emailVerified ? (
                  <span className="flex items-center gap-1.5 text-xs text-white/50">
                    <Check className="w-3 h-3" />
                    Verified
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 text-xs text-white/30">
                    <AlertCircle className="w-3 h-3" />
                    Unverified
                  </span>
                )}

                {user.webAuthnEnabled && (
                  <span className="flex items-center gap-1.5 text-xs text-white/50">
                    <Key className="w-3 h-3" />
                    Passkey
                  </span>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Tab Navigation */}
        <div className="flex gap-8 border-b border-white/5 mb-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              data-testid={`${tab.id}-tab`}
              onClick={() => setActiveTab(tab.id)}
              className={`
                pb-4 text-sm font-light tracking-wide transition-colors duration-300 relative
                ${activeTab === tab.id ? 'text-white' : 'text-white/30 hover:text-white/50'}
              `}
            >
              {tab.label}
              {activeTab === tab.id && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute bottom-0 left-0 right-0 h-px bg-white"
                />
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'identity' && (
            <motion.div
              key="identity"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-6"
            >
              {/* ΛiD Display */}
              <GlassCard className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs text-white/30 uppercase tracking-widest">
                    {tNested('dashboard', 'identity', 'yourIdentifier')}
                  </span>
                </div>
                <p className="text-2xl font-mono font-light text-white/80 tracking-wide">
                  {user.username || tNested('dashboard', 'identity', 'defaultId')}
                </p>
              </GlassCard>

              {/* Account Info */}
              <GlassCard className="p-6 space-y-4">
                <span className="text-xs text-white/30 uppercase tracking-widest">
                  {tNested('dashboard', 'identity', 'account')}
                </span>

                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2 border-b border-white/5">
                    <span className="text-sm text-white/50 font-light">{tNested('dashboard', 'identity', 'memberSince')}</span>
                    <span className="text-sm text-white/70 font-light">
                      {new Date(user.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </div>

                  <div className="flex items-center justify-between py-2 border-b border-white/5">
                    <span className="text-sm text-white/50 font-light">{tNested('dashboard', 'identity', 'accountType')}</span>
                    <span className="text-sm text-white/70 font-light">{tNested('dashboard', 'identity', 'personal')}</span>
                  </div>

                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-white/50 font-light">{tNested('dashboard', 'identity', 'email')}</span>
                    <span className="text-sm text-white/70 font-light">{user.email}</span>
                  </div>
                </div>
              </GlassCard>
            </motion.div>
          )}

          {activeTab === 'wallet' && (
            <motion.div
              key="wallet"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-6"
            >
              {/* Identity Pass description */}
              <GlassCard className="p-6">
                <div className="flex items-start gap-4 mb-6">
                  <CreditCard className="w-5 h-5 text-white/30 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-white/30 uppercase tracking-widest mb-1">
                      {tNested('dashboard', 'wallet', 'sectionLabel')}
                    </p>
                    <p className="text-sm text-white/50 font-light leading-relaxed">
                      {tNested('dashboard', 'wallet', 'description')}
                    </p>
                  </div>
                </div>

                {/* Add-to-Wallet buttons */}
                <WalletSignInPanel mode="manage" />
              </GlassCard>
            </motion.div>
          )}

          {activeTab === 'security' && (
            <motion.div
              key="security"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-6"
            >
              {/* Authentication Methods */}
              <GlassCard className="p-6">
                <span className="text-xs text-white/30 uppercase tracking-widest block mb-6">
                  {tNested('dashboard', 'security', 'authentication')}
                </span>

                <div className="space-y-4">
                  {/* Passphrase */}
                  <button className="w-full flex items-center justify-between p-4 border border-white/5 hover:border-white/10 transition-colors duration-300 group">
                    <div className="flex items-center gap-4">
                      <Lock className="w-5 h-5 text-white/30" />
                      <div className="text-left">
                        <p className="text-sm text-white/80 font-light">{tDeep('dashboard', 'security', 'passphrase', 'title')}</p>
                        <p className="text-xs text-white/30 font-light">{tDeep('dashboard', 'security', 'passphrase', 'description')}</p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-white/20 group-hover:text-white/40 transition-colors" />
                  </button>

                  {/* Passkey */}
                  <button className="w-full flex items-center justify-between p-4 border border-white/5 hover:border-white/10 transition-colors duration-300 group">
                    <div className="flex items-center gap-4">
                      <Key className="w-5 h-5 text-white/30" />
                      <div className="text-left">
                        <p className="text-sm text-white/80 font-light">{tDeep('dashboard', 'security', 'passkey', 'title')}</p>
                        <p className="text-xs text-white/30 font-light">
                          {user.webAuthnEnabled ? 'Configured' : 'Not configured'}
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-white/20 group-hover:text-white/40 transition-colors" />
                  </button>
                </div>
              </GlassCard>

              {/* Active Sessions - Full session management */}
              <GlassCard className="p-6">
                <AidSessions />
              </GlassCard>

              {/* Account Actions */}
              <GlassCard className="p-6">
                <span className="text-xs text-white/30 uppercase tracking-widest block mb-6">
                  {tNested('dashboard', 'security', 'accountActions')}
                </span>

                <button className="text-sm text-white/30 hover:text-white/50 transition-colors duration-300 font-light">
                  {tNested('dashboard', 'security', 'exportData')}
                </button>
              </GlassCard>
            </motion.div>
          )}

          {activeTab === 'connections' && (
            <motion.div
              key="connections"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-6"
            >
              {/* Connected Services */}
              <GlassCard className="p-6">
                <span className="text-xs text-white/30 uppercase tracking-widest block mb-6">
                  {tNested('dashboard', 'connections', 'ecosystem')}
                </span>

                <div className="space-y-3">
                  <div className="flex items-center justify-between p-4 border border-white/5">
                    <div className="flex items-center gap-4">
                      <Globe className="w-5 h-5 text-white/30" />
                      <div>
                        <p className="text-sm text-white/80 font-light">{tDeeper('dashboard', 'connections', 'services', 'lukhasAi', 'name')}</p>
                        <p className="text-xs text-white/30 font-light">{tDeeper('dashboard', 'connections', 'services', 'lukhasAi', 'description')}</p>
                      </div>
                    </div>
                    <span className="text-xs text-white/40">Connected</span>
                  </div>

                  <div className="flex items-center justify-between p-4 border border-white/5">
                    <div className="flex items-center gap-4">
                      <Globe className="w-5 h-5 text-white/30" />
                      <div>
                        <p className="text-sm text-white/80 font-light">{tDeeper('dashboard', 'connections', 'services', 'lukhasDev', 'name')}</p>
                        <p className="text-xs text-white/30 font-light">{tDeeper('dashboard', 'connections', 'services', 'lukhasDev', 'description')}</p>
                      </div>
                    </div>
                    <span className="text-xs text-white/40">Connected</span>
                  </div>

                  <div className="flex items-center justify-between p-4 border border-white/5">
                    <div className="flex items-center gap-4">
                      <Globe className="w-5 h-5 text-white/30" />
                      <div>
                        <p className="text-sm text-white/80 font-light">{tDeeper('dashboard', 'connections', 'services', 'lukhasCloud', 'name')}</p>
                        <p className="text-xs text-white/30 font-light">{tDeeper('dashboard', 'connections', 'services', 'lukhasCloud', 'description')}</p>
                      </div>
                    </div>
                    <span className="text-xs text-white/40">Connected</span>
                  </div>
                </div>
              </GlassCard>

              {/* Note about ecosystem */}
              <p className="text-xs text-white/20 font-light text-center">
                {tNested('dashboard', 'connections', 'ecosystemNote')}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
    </div>
  )
}
